package com.bikompanion.ui.settings

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.AbsoluteRoundedCornerShape
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.Mail
import androidx.compose.material.icons.filled.Map
import androidx.compose.material.icons.filled.PrivacyTip
import androidx.compose.material.icons.filled.Public
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.UriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.LifecycleStartEffect
import com.mikepenz.aboutlibraries.Libs
import com.mikepenz.aboutlibraries.entity.Library
import com.mikepenz.aboutlibraries.entity.License
import dev.chrisbanes.haze.HazeState
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import com.bikompanion.domain.LanguagePreference
import com.bikompanion.domain.ThemeMode
import com.bikompanion.domain.catchingRequest
import com.bikompanion.ui.composables.GlassWindow
import com.bikompanion.ui.install.AppInstallOffer
import com.bikompanion.ui.install.hint
import com.bikompanion.ui.install.icon
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewLightDarkMedium
import com.bikompanion.ui.preview.PreviewSurface
import com.bikompanion.ui.strings.BikompanionStrings
import com.bikompanion.ui.strings.LocalStrings

private const val AppName = "Bikompanion"
private const val FeedbackEmail = "contact@bikompanion.com"
private const val PrivacyEmail = "privacy@bikompanion.com"
private const val PrivacyPolicyUrl = "https://bikompanion.com/privacy.html"
private const val SegmentCornerPercent = 50

private val RowShape = RoundedCornerShape(16.dp)

/**
 * The app's settings, drawn as a [GlassWindow] over the map, with its pages swapped in place so
 * going deeper never leaves the window.
 *
 * [page] is null while it is closed.
 */
@Composable
internal fun SettingsWindow(
    page: SettingsPage?,
    themeMode: ThemeMode,
    platform: SettingsPlatform,
    hazeState: HazeState,
    floating: Boolean,
    language: LanguagePreference,
    onThemeModeSelect: (ThemeMode) -> Unit,
    onLanguageSelect: (LanguagePreference) -> Unit,
    onShareApp: () -> Unit,
    onPageOpen: (SettingsPage) -> Unit,
    onBack: () -> Unit,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
    appInstallOffer: AppInstallOffer? = null,
) {
    // The exit animation still needs a page to draw once `page` has gone null.
    var shownPage by remember { mutableStateOf(page ?: SettingsPage.Main) }
    if (page != null) shownPage = page
    GlassWindow(
        visible = page != null,
        hazeState = hazeState,
        floating = floating,
        onBack = onBack,
        onDismiss = onClose,
        modifier = modifier,
    ) {
        SettingsHeader(page = shownPage, onBack = onBack, onClose = onClose)
        AnimatedContent(
            targetState = shownPage,
            transitionSpec = {
                val direction = if (targetState.ordinal > initialState.ordinal) 1 else -1
                (slideInHorizontally { it / 6 * direction } + fadeIn())
                    .togetherWith(slideOutHorizontally { -it / 6 * direction } + fadeOut())
            },
            modifier = Modifier.weight(1f),
        ) { current ->
            when (current) {
                SettingsPage.Main -> MainPage(
                    themeMode = themeMode,
                    language = language,
                    notifications = platform.notifications,
                    appInstallOffer = appInstallOffer,
                    onThemeModeSelect = onThemeModeSelect,
                    onLanguageSelect = onLanguageSelect,
                    onShareApp = onShareApp,
                    onAboutClick = { onPageOpen(SettingsPage.About) },
                )

                SettingsPage.About -> AboutPage(
                    versionName = platform.versionName,
                    onLicensesClick = { onPageOpen(SettingsPage.Licenses) }
                        .takeIf { platform.loadLicenses != null },
                    loadInstallId = platform.loadInstallId,
                )

                SettingsPage.Licenses -> LicensesPage(loadLicenses = platform.loadLicenses)
            }
        }
    }
}

@Composable
private fun SettingsHeader(
    page: SettingsPage,
    onBack: () -> Unit,
    onClose: () -> Unit,
) {
    val strings = LocalStrings.current
    Row(
        modifier = Modifier.fillMaxWidth().padding(4.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (page.parent != null) {
            IconButton(onClick = onBack) {
                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = strings.actionBack)
            }
        }
        Text(
            text = page.title(strings),
            style = MaterialTheme.typography.titleLarge,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier
                .weight(1f)
                .padding(start = if (page.parent == null) 16.dp else 4.dp),
        )
        IconButton(onClick = onClose) {
            Icon(Icons.Default.Close, contentDescription = strings.actionClose)
        }
    }
}

private fun SettingsPage.title(strings: BikompanionStrings): String =
    when (this) {
        SettingsPage.Main -> strings.settingsTitle
        SettingsPage.About -> strings.settingsAbout
        SettingsPage.Licenses -> strings.licensesTitle
    }

@Composable
private fun PageColumn(content: @Composable ColumnScope.() -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(start = 16.dp, end = 16.dp, top = 4.dp, bottom = 24.dp),
        verticalArrangement = Arrangement.spacedBy(20.dp),
        content = content,
    )
}

@Composable
private fun SettingsGroup(
    title: String,
    content: @Composable ColumnScope.() -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            text = title,
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.primary,
            modifier = Modifier.padding(start = 4.dp, bottom = 4.dp),
        )
        content()
    }
}

@Composable
private fun MainPage(
    themeMode: ThemeMode,
    language: LanguagePreference,
    notifications: NotificationControls?,
    appInstallOffer: AppInstallOffer?,
    onThemeModeSelect: (ThemeMode) -> Unit,
    onLanguageSelect: (LanguagePreference) -> Unit,
    onShareApp: () -> Unit,
    onAboutClick: () -> Unit,
) {
    val uriHandler = LocalUriHandler.current
    val strings = LocalStrings.current
    PageColumn {
        if (appInstallOffer != null) {
            SettingsGroup(strings.actionInstall) {
                ActionRow(
                    icon = appInstallOffer.icon,
                    text = strings.installTitle,
                    description = appInstallOffer.hint(strings),
                    // iOS has nothing to open: the steps in the description are the whole offer.
                    onClick = (appInstallOffer as? AppInstallOffer.OpenStore)?.let { store ->
                        { uriHandler.openIfPossible(store.url) }
                    },
                )
            }
        }
        SettingsGroup(strings.settingsAppearance) {
            ThemeSelector(selected = themeMode, onSelect = onThemeModeSelect)
            LanguageSelector(selected = language, onSelect = onLanguageSelect)
        }
        if (notifications != null) {
            SettingsGroup(strings.settingsNotifications) {
                NotificationRow(controls = notifications)
            }
        }
        SettingsGroup(strings.settingsSupport) {
            ActionRow(
                icon = Icons.Default.Mail,
                text = strings.sendFeedback,
                description = strings.sendFeedbackDescription,
                onClick = { uriHandler.openIfPossible("mailto:$FeedbackEmail?subject=$AppName%20feedback") },
            )
            ActionRow(icon = Icons.Default.Share, text = strings.shareApp, onClick = onShareApp)
        }
        SettingsGroup(strings.settingsAbout) {
            ActionRow(icon = Icons.Default.Info, text = strings.aboutApp, onClick = onAboutClick)
        }
    }
}

@Composable
private fun ThemeSelector(
    selected: ThemeMode,
    onSelect: (ThemeMode) -> Unit,
) {
    val strings = LocalStrings.current
    val layoutDirection = LocalLayoutDirection.current
    SingleChoiceSegmentedButtonRow(modifier = Modifier.fillMaxWidth()) {
        ThemeMode.entries.forEachIndexed { index, mode ->
            SegmentedButton(
                selected = mode == selected,
                onClick = { onSelect(mode) },
                shape = segmentShape(index, ThemeMode.entries.size, layoutDirection),
                label = { Text(mode.label(strings)) },
            )
        }
    }
}

/**
 * Physical corners rather than `SegmentedButtonDefaults.itemShape`, whose start and end corners came
 * out swapped in Arabic: the buttons were placed right to left but rounded on their left-to-right
 * sides, leaving the row's outer corners square.
 */
private fun segmentShape(
    index: Int,
    count: Int,
    layoutDirection: LayoutDirection,
): Shape {
    val rtl = layoutDirection == LayoutDirection.Rtl
    val atLeftEdge = index == if (rtl) count - 1 else 0
    val atRightEdge = index == if (rtl) 0 else count - 1
    val left = if (atLeftEdge) SegmentCornerPercent else 0
    val right = if (atRightEdge) SegmentCornerPercent else 0
    return AbsoluteRoundedCornerShape(
        topLeftPercent = left,
        topRightPercent = right,
        bottomRightPercent = right,
        bottomLeftPercent = left,
    )
}

private fun ThemeMode.label(strings: BikompanionStrings): String =
    when (this) {
        ThemeMode.Auto -> strings.themeSystem
        ThemeMode.Light -> strings.themeLight
        ThemeMode.Dark -> strings.themeDark
    }

@Composable
private fun LanguageSelector(
    selected: LanguagePreference,
    onSelect: (LanguagePreference) -> Unit,
) {
    val strings = LocalStrings.current
    var expanded by remember { mutableStateOf(false) }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .rowSurface()
            .clickable { expanded = true }
            .padding(horizontal = 16.dp, vertical = 12.dp),
    ) {
        Text(
            text = strings.settingsLanguage,
            style = MaterialTheme.typography.bodyLarge,
            modifier = Modifier.weight(1f).padding(end = 12.dp),
        )
        Box {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = selected.label(strings),
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Icon(
                    imageVector = Icons.Default.KeyboardArrowDown,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
                LanguagePreference.entries.forEach { language ->
                    DropdownMenuItem(
                        text = { Text(language.label(strings)) },
                        onClick = {
                            onSelect(language)
                            expanded = false
                        },
                        trailingIcon = if (language == selected) {
                            { Icon(Icons.Default.Check, contentDescription = null) }
                        } else {
                            null
                        },
                    )
                }
            }
        }
    }
}

private fun LanguagePreference.label(strings: BikompanionStrings): String =
    if (this == LanguagePreference.System) strings.languageSystem else displayName

/** Reads the status each time the app comes back, since it changes in the system's own settings. */
@Composable
private fun NotificationRow(controls: NotificationControls) {
    val strings = LocalStrings.current
    var status by remember(controls) { mutableStateOf(controls.status()) }
    LifecycleStartEffect(controls) {
        status = controls.status()
        onStopOrDispose {}
    }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.rowSurface().padding(start = 16.dp, end = 8.dp, top = 12.dp, bottom = 12.dp),
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(strings.stationAlerts, style = MaterialTheme.typography.bodyLarge)
            Text(
                text = status.description(strings),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        if (status != NotificationStatus.Blocked) {
            TextButton(onClick = { controls.manage { status = controls.status() } }) {
                Text(if (status == NotificationStatus.Allowed) strings.actionManage else strings.actionTurnOn)
            }
        }
    }
}

private fun NotificationStatus.description(strings: BikompanionStrings): String =
    when (this) {
        NotificationStatus.Allowed -> strings.notificationsOn
        NotificationStatus.Off -> strings.notificationsOff
        NotificationStatus.Blocked -> strings.notificationsBlocked
    }

@Composable
private fun AboutPage(
    versionName: String,
    onLicensesClick: (() -> Unit)?,
    loadInstallId: (suspend () -> String)?,
) {
    val uriHandler = LocalUriHandler.current
    val strings = LocalStrings.current
    val scope = rememberCoroutineScope()
    PageColumn {
        AboutHeader(versionName = versionName)
        SettingsGroup(strings.aboutDataSources) {
            ActionRow(
                icon = Icons.Default.Public,
                text = "CityBikes",
                description = strings.sourceCityBikesDescription,
                onClick = { uriHandler.openIfPossible("https://citybik.es") },
            )
            ActionRow(
                icon = Icons.Default.Public,
                text = strings.sourceGbfsTitle,
                description = strings.sourceGbfsDescription,
                onClick = { uriHandler.openIfPossible("https://gbfs.org") },
            )
            ActionRow(
                icon = Icons.Default.Map,
                text = strings.sourceOsmTitle,
                description = strings.sourceOsmDescription,
                onClick = { uriHandler.openIfPossible("https://www.openstreetmap.org/copyright") },
            )
            ActionRow(
                icon = Icons.Default.Map,
                text = "CARTO",
                description = strings.sourceCartoDescription,
                onClick = { uriHandler.openIfPossible("https://carto.com/attributions") },
            )
        }
        SettingsGroup(strings.aboutLegal) {
            ActionRow(
                icon = Icons.Default.PrivacyTip,
                text = strings.privacyPolicy,
                onClick = { uriHandler.openIfPossible(PrivacyPolicyUrl) },
            )
            if (loadInstallId != null) {
                ActionRow(
                    icon = Icons.Default.Delete,
                    text = strings.deleteRatings,
                    description = strings.deleteRatingsDescription,
                    onClick = {
                        scope.launch {
                            val installId = catchingRequest { loadInstallId() }.getOrNull()
                            uriHandler.openIfPossible(ratingDeletionEmail(installId))
                        }
                    },
                )
            }
            if (onLicensesClick != null) {
                ActionRow(
                    icon = Icons.Default.Description,
                    text = strings.licensesTitle,
                    onClick = onLicensesClick,
                )
            }
        }
    }
}

/**
 * Written to us rather than to the rider, so it stays in English whatever the app's language.
 * Without an ID (sign-in failed, typically offline) the email still goes out, and the reply asks
 * for it.
 */
internal fun ratingDeletionEmail(installId: String?): String {
    val body = if (installId != null) {
        "Please delete every rating made with this install ID: $installId"
    } else {
        "Please delete my ratings."
    }
    return "mailto:$PrivacyEmail?subject=$AppName%20rating%20deletion&body=${body.replace(" ", "%20")}"
}

@Composable
private fun AboutHeader(versionName: String) {
    val strings = LocalStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(
            text = AppName,
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.SemiBold,
        )
        Text(
            text = strings.aboutVersion(versionName),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = strings.aboutDevelopedBy,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun LicensesPage(loadLicenses: (suspend () -> String)?) {
    val libraries by produceState<Result<List<Library>>?>(initialValue = null, loadLicenses) {
        value = catchingRequest {
            val json = checkNotNull(loadLicenses).invoke()
            Libs
                .Builder()
                .withJson(json)
                .build()
                .libraries
        }
    }
    val loaded = libraries
    when {
        loaded == null -> CenteredPageContent { CircularProgressIndicator() }
        loaded.isFailure -> LicensesLoadFailed()
        else -> LicenseList(libraries = loaded.getOrDefault(emptyList()))
    }
}

@Composable
private fun LicensesLoadFailed() {
    CenteredPageContent {
        Text(
            text = LocalStrings.current.licensesLoadFailed,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun CenteredPageContent(content: @Composable () -> Unit) {
    Box(
        modifier = Modifier.fillMaxSize().padding(24.dp),
        contentAlignment = Alignment.Center,
    ) {
        content()
    }
}

@Composable
private fun LicenseList(libraries: List<Library>) {
    val uriHandler = LocalUriHandler.current
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 4.dp, bottom = 24.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        items(libraries, key = { it.uniqueId }) { library ->
            val website = library.website
            LicenseRow(
                library = library,
                onClick = website?.let { { uriHandler.openIfPossible(it) } },
            )
        }
    }
}

@Composable
private fun LicenseRow(
    library: Library,
    onClick: (() -> Unit)?,
) {
    Column(
        modifier = Modifier
            .rowSurface()
            .then(if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier)
            .padding(horizontal = 16.dp, vertical = 12.dp),
    ) {
        Text(text = library.name, style = MaterialTheme.typography.bodyLarge)
        Text(
            text = listOfNotNull(
                library.artifactVersion,
                library.licenses.joinToString { it.name }.ifEmpty { null },
            ).joinToString(" · "),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun ActionRow(
    icon: ImageVector,
    text: String,
    onClick: (() -> Unit)?,
    description: String? = null,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .rowSurface()
            .then(if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier)
            .padding(horizontal = 16.dp, vertical = 12.dp),
    ) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .size(36.dp)
                .clip(CircleShape)
                .background(MaterialTheme.colorScheme.primaryContainer),
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onPrimaryContainer,
                modifier = Modifier.size(20.dp),
            )
        }
        Column(modifier = Modifier.weight(1f).padding(horizontal = 16.dp)) {
            Text(text = text, style = MaterialTheme.typography.bodyLarge)
            if (description != null) {
                Text(
                    text = description,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        if (onClick != null) {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun Modifier.rowSurface(): Modifier =
    this
        .fillMaxWidth()
        .heightIn(min = 56.dp)
        .clip(RowShape)
        .background(MaterialTheme.colorScheme.surfaceContainerHigh.copy(alpha = 0.7f))

/** A device with no mail app has nothing to open `mailto:` with, which is no reason to crash. */
private fun UriHandler.openIfPossible(uri: String) {
    try {
        openUri(uri)
    } catch (_: IllegalArgumentException) {
        return
    }
}

private fun previewPlatform(status: NotificationStatus) =
    SettingsPlatform(
        versionName = "1.0.0",
        notifications = NotificationControls(status = { status }, manage = {}),
    )

private fun previewLibrary(
    name: String,
    website: String?,
) = Library(
    uniqueId = name,
    artifactVersion = "1.0.0",
    name = name,
    description = null,
    website = website,
    developers = emptyList(),
    organization = null,
    scm = null,
    licenses = setOf(License(name = "Apache 2.0", url = null, hash = "apache-2.0")),
)

@PreviewLightDarkCompact
@Composable
private fun SettingsWindowNotificationsOffPreview() {
    SettingsWindowPreview(page = SettingsPage.Main, platform = previewPlatform(NotificationStatus.Off))
}

@PreviewLightDarkCompact
@Composable
private fun SettingsWindowNotificationsAllowedPreview() {
    SettingsWindowPreview(
        page = SettingsPage.Main,
        platform = previewPlatform(NotificationStatus.Allowed),
        themeMode = ThemeMode.Light,
    )
}

/** No button: a blocked permission can only be changed outside the app. */
@PreviewLightDarkCompact
@Composable
private fun SettingsWindowNotificationsBlockedPreview() {
    SettingsWindowPreview(
        page = SettingsPage.Main,
        platform = previewPlatform(NotificationStatus.Blocked),
        themeMode = ThemeMode.Dark,
    )
}

/** A platform with no notification permission to manage. */
@PreviewLightDarkCompact
@Composable
private fun SettingsWindowNoNotificationsPreview() {
    SettingsWindowPreview(page = SettingsPage.Main, platform = SettingsPlatform(versionName = "1.0.0"))
}

/** A row with nothing to open, so no chevron: the steps are the offer. */
@PreviewLightDarkCompact
@Composable
private fun SettingsWindowInstallIosPreview() {
    SettingsWindowPreview(
        page = SettingsPage.Main,
        platform = SettingsPlatform(versionName = "1.0.0"),
        appInstallOffer = AppInstallOffer.AddToHomeScreen,
    )
}

@PreviewLightDarkCompact
@Composable
private fun SettingsWindowInstallAndroidPreview() {
    SettingsWindowPreview(
        page = SettingsPage.Main,
        platform = SettingsPlatform(versionName = "1.0.0"),
        appInstallOffer = AppInstallOffer.OpenStore("https://play.google.com/"),
    )
}

@PreviewLightDarkMedium
@Composable
private fun SettingsWindowFloatingPreview() {
    SettingsWindowPreview(page = SettingsPage.Main, platform = previewPlatform(NotificationStatus.Off), floating = true)
}

@PreviewLightDarkCompact
@Composable
private fun SettingsWindowAboutPreview() {
    SettingsWindowPreview(
        page = SettingsPage.About,
        platform = SettingsPlatform(versionName = "1.0.0", loadLicenses = { "" }, loadInstallId = { "" }),
    )
}

/** A platform with no license list to show, so the row leading to one goes too. */
@PreviewLightDarkMedium
@Composable
private fun SettingsWindowAboutNoLicensesPreview() {
    SettingsWindowPreview(page = SettingsPage.About, platform = SettingsPlatform(versionName = "1.0.0"), floating = true)
}

@PreviewLightDarkCompact
@Composable
private fun SettingsWindowLicensesLoadingPreview() {
    SettingsWindowPreview(
        page = SettingsPage.Licenses,
        platform = SettingsPlatform(versionName = "1.0.0", loadLicenses = { awaitCancellation() }),
    )
}

// The page itself only gets past loading once an effect has run, so its two results are shown directly.
@Preview
@Composable
private fun LicensesLoadFailedPreview() {
    PreviewSurface {
        LicensesLoadFailed()
    }
}

/** The second row has no website, so it is the one that doesn't click through. */
@PreviewLightDarkCompact
@Composable
private fun LicenseListPreview() {
    PreviewSurface {
        LicenseList(
            libraries = listOf(
                previewLibrary("Compose Multiplatform", website = "https://www.jetbrains.com/compose-multiplatform"),
                previewLibrary("Haze", website = null),
            ),
        )
    }
}

@Composable
private fun SettingsWindowPreview(
    page: SettingsPage,
    platform: SettingsPlatform,
    themeMode: ThemeMode = ThemeMode.Auto,
    floating: Boolean = false,
    appInstallOffer: AppInstallOffer? = null,
) {
    GlassPreviewSurface { hazeState ->
        SettingsWindow(
            page = page,
            themeMode = themeMode,
            platform = platform,
            hazeState = hazeState,
            floating = floating,
            onThemeModeSelect = {},
            language = LanguagePreference.System,
            onLanguageSelect = {},
            onShareApp = {},
            onPageOpen = {},
            onBack = {},
            onClose = {},
            appInstallOffer = appInstallOffer,
        )
    }
}
